import { Configuration } from "@vintecc/capture-api-types/v1";
import { createAxiosInterceptor } from "./api.interceptor";
import axios, { AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { useNetworkStore } from "@/shared/store/networkStore";

// 1. Create a placeholder callback function to retrieve the company view
let getCompanyViewCallback: () => string = () => "";

// 2. Export a function to register this callback later
export const registerCompanyViewGetter = (getter: () => string) => {
  getCompanyViewCallback = getter;
};

interface RequestMetadata {
  startTime: number;
}

type InternalAxiosRequestConfigExtended = InternalAxiosRequestConfig & {
  metadata?: RequestMetadata;
};

const captureApiConfiguration = new Configuration({
  basePath: window.location.origin,
});

function tryParse(data: unknown) {
  if (typeof data !== "string") return data;
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
}

const responseInterceptor = (response: AxiosResponse) => {
  const start = (response.config as InternalAxiosRequestConfigExtended).metadata?.startTime;
  useNetworkStore.getState().addNetworkLog({
    id: crypto.randomUUID(),
    endpoint: response.config.url ?? "",
    method: (response.config.method ?? "GET").toUpperCase(),
    timestamp: new Date(),
    params: response.config.params,
    body: tryParse(response.config.data),
    status: response.status,
    response: response.data,
    duration: start ? Date.now() - start : undefined,
  });
  return response;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorInterceptor = (error: any) => {
  const config = (error.config as InternalAxiosRequestConfigExtended) ?? {};
  const start = config.metadata?.startTime;
  useNetworkStore.getState().addNetworkLog({
    id: crypto.randomUUID(),
    endpoint: config.url ?? "",
    method: (config.method ?? "GET").toUpperCase(),
    timestamp: new Date(),
    params: config.params,
    body: tryParse(config.data),
    status: error.response?.status,
    response: error.response?.data,
    error: error.message,
    duration: start ? Date.now() - start : undefined,
  });
  return Promise.reject(error);
};

axios.interceptors.request.use((config) => {
  if (config.headers["CompanyView"] === undefined) {
    // 3. Use the callback here instead of importing the store
    const currentCompany = getCompanyViewCallback();
    if (currentCompany) {
      config.headers["CompanyView"] = currentCompany;
    }
  }

  // Attach the request start time to the config for later use in response interceptor
  (config as InternalAxiosRequestConfigExtended).metadata = { startTime: Date.now() };

  return config;
});

axios.interceptors.response.use(responseInterceptor, errorInterceptor);
createAxiosInterceptor(axios);

export default captureApiConfiguration;
